import { getUserAccessPoliciesAsync } from '@chr/online-shared-microsite-core';
import { ApplicationIds } from '@chr/online-shared-ui-constants';
import { PermissionIdType, PermissionsByPartyCode } from "../constants/permission-types";

// The new Users API consolidates access policies by entityCode, 
// so we no longer need to handle relatedEntities separately.
// We still filter by entity.code === partyCode to select only policies relevant to the given party.
// Only policies with defined permissionIds are included.
export const getPermissionSet = async (partyCode: string): Promise<Set<PermissionIdType>> =>
  new Set(
    ((await getUserAccessPoliciesAsync(ApplicationIds.Online2)) ?? [])
      .flatMap(p => (p.entity?.code === partyCode && p.permissionIds) ? p.permissionIds : [])
  ); 

export const getPermissionSetsByPartyCode = async (partyCodes: string[]): Promise<Map<string, Set<PermissionIdType>>> => {
  const permissionSets = await Promise.all(partyCodes.map(partyCode => getPermissionSet(partyCode)));

  return partyCodes.reduce((acc, partyCode, index) =>
    acc.set(partyCode, permissionSets[index]), new Map<string, Set<PermissionIdType>>());
}

export const getPermissionsByPartyCode = async (partyCodes: string[]): Promise<PermissionsByPartyCode> => {
  const permissionSets = await getPermissionSetsByPartyCode(partyCodes);

  return partyCodes.reduce((acc, partyCode) => {
    const permissionSet = permissionSets.get(partyCode);

    if (permissionSet) {
      acc[partyCode] = {
        hasEditOrder: permissionSet.has(PermissionIdType.EDIT_ORDER),
        hasCreateOrder: permissionSet.has(PermissionIdType.CREATE_ORDER),
        hasViewBol: permissionSet.has(PermissionIdType.VIEW_BOL),
        hasViewCmr: permissionSet.has(PermissionIdType.VIEW_CMR),
        hasSmallParcelRate: permissionSet.has(PermissionIdType.SMALL_PARCEL_PRINT_PACKAGE_LABELS_AND_RATE_SHOP),
        hasSmallParcelLabels: permissionSet.has(PermissionIdType.SMALL_PARCEL_PRINT_PACKAGE_LABELS),
        hasFreightCharges: permissionSet.has(PermissionIdType.FREIGHT_CHARGES),
        hasBaseLineFreightCharges: permissionSet.has(PermissionIdType.BASELINE_FREIGHT_CHARGES),
        hasEndCustomerCharges: permissionSet.has(PermissionIdType.END_CUSTOMER_CHARGES),
        hasSuggestedCarrier: permissionSet.has(PermissionIdType.SUGGESTED_CARRIER),
        hasViewOtherDocuments: permissionSet.has(PermissionIdType.VIEW_OTHER_DOCUMENTS)
      };
    }

    return acc;
  }, {} as PermissionsByPartyCode);
}
